import { createFileRoute, Link } from "@tanstack/react-router"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"

import styles from "./index.module.css"

export const Route = createFileRoute("/qr/$qrLookupID/dev/")({
  staticData: {
    pageName: "Component Library",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { qrLookupID } = Route.useParams()

  return (
    <div className={styles.container}>
      <Card>
        <Heading level={1} as={2}>
          Component Library Examples
        </Heading>
        <Text>
          This section contains working examples and documentation for the Tap N
          Samba component library. Each example demonstrates component usage,
          props, and common patterns.
        </Text>
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Gut Check
        </Heading>
        <Text className={styles.sectionDescription}>
          View all components together to verify theming
        </Text>
        <div className={styles.linksContainer}>
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/gut-check`}
            title="Gut Check"
            description="All components densely packed for theme verification"
          />
        </div>
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Atoms
        </Heading>
        <Text className={styles.sectionDescription}>
          Basic building blocks of the UI
        </Text>
        <div className={styles.linksContainer}>
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/atoms/button`}
            title="Button"
            description="Primary, secondary, and link button variations"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/atoms/typography`}
            title="Typography"
            description="Typography components with different levels and styles"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/atoms/icon`}
            title="Icon"
            description="Icon library and usage"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/atoms/card`}
            title="Card"
            description="Container component for content grouping"
          />
        </div>
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Molecules
        </Heading>
        <Text className={styles.sectionDescription}>
          Composed components combining atoms
        </Text>
        <div className={styles.linksContainer}>
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/molecules/forms`}
            title="Forms"
            description="TextField, Select, and VehicleSelectorField components"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/molecules/modal`}
            title="Modal"
            description="Dialog and modal patterns"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/molecules/range-calendar`}
            title="RangeCalendar"
            description="Contiguous start-to-end date selection"
          />
        </div>
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Organisms
        </Heading>
        <Text className={styles.sectionDescription}>
          Complex components and layouts
        </Text>
        <div className={styles.linksContainer}>
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/organisms/navigation`}
            title="Navigation"
            description="TopNavigationBar, Drawer, and Footer components"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/organisms/flow-card`}
            title="FlowCard"
            description="Parking and FlexPurchase flow cards"
          />
          <ComponentLink
            to={`/qr/${qrLookupID}/dev/organisms/reservation-rate-card`}
            title="ReservationRateCard"
            description="Every membership, promotion, and state variant of the reserve rate card"
          />
        </div>
      </Card>
    </div>
  )
}

interface ComponentLinkProps {
  to: string
  title: string
  description: string
}

function ComponentLink({ to, title, description }: ComponentLinkProps) {
  return (
    <Link to={to} className={styles.componentLink}>
      <Text level={2} className="semi-bold">
        {title}
      </Text>
      <Text level={3}>{description}</Text>
    </Link>
  )
}
